// The booking confirmation's outbox (M2, DESK-OPERATOR-AND-MAIL-BRIEF §3.2) —
// `mail_outbox`. Browser-scoped: the desk's client. Writes are two RPCs
// (pms.reservation.write, in reach, the person stamped); reads are the table
// under RLS.

import type {
  ConfirmationMail,
  ConfirmationMailDraft,
  ConfirmationMailLang,
  ConfirmationMailRepository,
  ConfirmationMailStatus,
  Result,
} from '@lemontaigu/pms-core';
import { fromList, fromResult, fromVoid, type SupabaseClient } from './support.js';
import { ok } from '@lemontaigu/pms-core';

interface MailOutboxRow {
  id: string;
  reservation_id: string;
  property_code: string;
  to_email: string;
  lang: ConfirmationMailLang;
  subject: string;
  status: ConfirmationMailStatus;
  card_link: string | null;
  attempts: number;
  error: string | null;
  prepared_at: string;
  sent_at: string | null;
}

const COLUMNS = 'id,reservation_id,property_code,to_email,lang,subject,status,card_link,attempts,error,prepared_at,sent_at';

export const rowToConfirmationMail = (r: MailOutboxRow): ConfirmationMail => ({
  id: r.id,
  reservationId: r.reservation_id,
  propertyCode: r.property_code,
  to: r.to_email,
  lang: r.lang,
  subject: r.subject,
  status: r.status,
  cardLink: r.card_link,
  attempts: r.attempts,
  error: r.error,
  preparedAt: r.prepared_at,
  sentAt: r.sent_at,
});

export class SupabaseConfirmationMailRepository implements ConfirmationMailRepository {
  constructor(private readonly db: SupabaseClient) {}

  async prepare(d: ConfirmationMailDraft): Promise<Result<string>> {
    return fromResult(
      await this.db.rpc('mail_confirmation_prepare', {
        p_reservation: d.reservationId,
        p_to: d.to,
        p_lang: d.lang,
        p_subject: d.subject,
        p_body: d.body,
      }),
    );
  }

  async setLink(id: string, link: string): Promise<Result<void>> {
    return fromVoid(await this.db.rpc('mail_confirmation_set_link', { p_id: id, p_link: link }));
  }

  async latest(reservationId: string): Promise<Result<ConfirmationMail | null>> {
    const r = await fromList(
      await this.db
        .from('mail_outbox')
        .select(COLUMNS)
        .eq('reservation_id', reservationId)
        .order('prepared_at', { ascending: false })
        .limit(1),
      rowToConfirmationMail,
    );
    return r.ok ? ok(r.value[0] ?? null) : r;
  }

  async pending(): Promise<Result<ConfirmationMail[]>> {
    return fromList(
      await this.db.from('mail_outbox').select(COLUMNS).neq('status', 'sent').order('prepared_at', { ascending: true }),
      rowToConfirmationMail,
    );
  }
}
